import { useCallback, useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getShipToOrderHistoryQuery } from '@setvi/shared/services';
import type { ModifiableProduct } from 'pages/sales/modules/orders/subpages/orders/subpages/order-create';
import { mapOrderToProduct } from '@setvi/shared/utils/order';

const PAGE_SIZE = 20;

export interface UseShipToOrderHistoryProps {
  productId: string;
  accountId: string;
  addressId?: string;
}

export const useShipToOrderHistory = ({
  accountId,
  addressId
}: UseShipToOrderHistoryProps) => {
  const [search, setSearch] = useState('');
  const [refreshedStock, setRefreshedStock] = useState<any>({});

  const query = useInfiniteQuery(
    getShipToOrderHistoryQuery({
      accountNumber: accountId,
      shipToId: addressId || '',
      searchStr: search,
      limit: PAGE_SIZE,
      offset: 0
    })
  );

  const products: ModifiableProduct[] = useMemo(
    () =>
      query.data?.pages?.flatMap(page =>
        page?.data.map(order =>
          mapOrderToProduct<ModifiableProduct>(
            order,
            refreshedStock[order.product._id]
          )
        )
      ) || [],
    [query.data, refreshedStock]
  );

  const isLoading = query.isFetching && !query.isFetchingNextPage;

  const { hasNextPage, fetchNextPage } = query;
  const fetchMore = useCallback(() => {
    if (hasNextPage) fetchNextPage();
  }, [hasNextPage, fetchNextPage]);

  const onStockRefresh = (
    id: ModifiableProduct['_id'],
    newValues: Partial<ModifiableProduct>
  ) => setRefreshedStock((prev: any) => ({ ...prev, [id]: newValues }));

  return {
    products,
    search,
    setSearch,
    isLoading,
    isFetchingMore: query.isFetchingNextPage,
    fetchMore,
    onStockRefresh
  };
};
